import React, { useState, useEffect, useRef } from 'react';
import {
  Upload, User, TrendingUp, GraduationCap,
  Edit2, Save, X, ExternalLink, Brain, RefreshCw, Printer, ChevronRight, BookmarkPlus, BookmarkCheck
} from 'lucide-react';
import { authFetch } from "../../api";
import useIsMobile from '../../useIsMobile';

const API = 'https://hs.aida-advisory.com';

const gpaColor  = g => !g ? '#546E7A' : g >= 3.5 ? '#00E676' : g >= 2.5 ? '#FFD600' : '#EF5350';
const eligColor = s => ({ eligible:'#00E676', at_risk:'#FFB300', ineligible:'#EF5350' }[s] || '#546E7A');
const eligLabel = s => ({ eligible:'NCAA Eligible', at_risk:'At Risk', ineligible:'Ineligible' }[s] || 'Unknown');
const fmtDOB    = d => { if(!d) return null; const p=d.split('-'); return p.length===3?`${p[1]}/${p[2]}/${p[0]}`:d; };

/* ── Report text renderer ─────────────────────────────────────────────────── */
const ReportContent = ({ text }) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    const t = line.trim();
    if (!t) return <div key={i} style={{height:6}}/>;
    const isHdr = t === t.toUpperCase() && t.length > 3 && /^[A-Z][A-Z\s&]+$/.test(t);
    if (isHdr) return (
      <div key={i} style={{marginTop:22,marginBottom:6,paddingBottom:4,borderBottom:'1px solid rgba(0,212,255,0.2)'}}>
        <span style={{color:'#00D4FF',fontWeight:700,fontSize:10,letterSpacing:'0.14em',textTransform:'uppercase'}}>{t}</span>
      </div>
    );
    return <p key={i} style={{color:'#C8D8E8',fontSize:13,lineHeight:1.78,marginBottom:8}}>{line}</p>;
  });
};

/* ── Stat chip for hero ───────────────────────────────────────────────────── */
const StatChip = ({ label, value, color }) => value ? (
  <div style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
    <div style={{color:'#546E7A',fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:2}}>{label}</div>
    <div style={{color:color||'#00D4FF',fontWeight:700,fontSize:15,fontFamily:'monospace',lineHeight:1}}>{value}</div>
  </div>
) : null;

/* ── Info cell for cards ──────────────────────────────────────────────────── */
const Cell = ({ label, value, color, big }) => (
  <div>
    <div style={{color:'#546E7A',fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:3}}>{label}</div>
    <div style={{color:color||'#D8E8F4',fontWeight:big?700:500,fontSize:big?22:13,fontFamily:big?'monospace':'inherit',lineHeight:big?1:1.3}}>
      {value ?? <span style={{color:'#263A50',fontSize:12,fontWeight:400}}>—</span>}
    </div>
  </div>
);

/* ── Modal field components ───────────────────────────────────────────────── */
const MF = ({ label, value, onChange, type='text', inputMode, placeholder, span }) => (
  <div style={span?{gridColumn:`span ${span}`}:{}}>
    <div style={{color:'#6A8EA8',fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:5}}>{label}</div>
    <input type={type} inputMode={inputMode} value={value||''} onChange={onChange} placeholder={placeholder||''}
      style={{width:'100%',background:'#06101C',color:'white',border:'1px solid rgba(0,212,255,0.18)',
        borderRadius:7,padding:'8px 11px',fontSize:13,outline:'none',boxSizing:'border-box'}}/>
  </div>
);
const MS = ({ label, value, onChange, children, span }) => (
  <div style={span?{gridColumn:`span ${span}`}:{}}>
    <div style={{color:'#6A8EA8',fontSize:10,textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:5}}>{label}</div>
    <select value={value||''} onChange={onChange}
      style={{width:'100%',background:'#06101C',color:'white',border:'1px solid rgba(0,212,255,0.18)',
        borderRadius:7,padding:'8px 11px',fontSize:13,outline:'none',boxSizing:'border-box'}}>
      {children}
    </select>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════ */
const PlayerProfile = ({ player, onPhotoUpload, onPlayerUpdate }) => {
  // This component lays itself out with inline styles, so it reads the
  // breakpoint directly instead of relying on responsive utility classes.
  const isMobile = useIsMobile();

  // Which measurables matter depends on the position, and the definitions live
  // on the server so the coach form and the family's form ask for the same
  // things. Falls back to the previous fixed set if the fetch fails.
  const [measurableDefs, setMeasurableDefs] = useState([]);
  useEffect(() => {
    if (!player?.position) return;
    let cancelled = false;
    fetch(`${API}/api/athlete/measurables/${encodeURIComponent(player.position)}`)
      .then(r => r.ok ? r.json() : [])
      .then(d => { if (!cancelled) setMeasurableDefs(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [player?.position]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [editOpen,       setEditOpen      ] = useState(false);
  const [ep,             setEp            ] = useState({});
  const [saving,         setSaving        ] = useState(false);
  const [aiSummary,      setAiSummary     ] = useState(() => { try { return localStorage.getItem(`scout_report_${player.id}`) || ''; } catch { return ''; } });
  const [onWatchlist, setOnWatchlist] = useState(() => {
    try {
      const list = JSON.parse(localStorage.getItem('academic_watchlist') || '[]');
      return list.some(p => p.id === player.id);
    } catch { return false; }
  });

  const toggleWatchlist = () => {
    try {
      const list = JSON.parse(localStorage.getItem('academic_watchlist') || '[]');
      const updated = onWatchlist
        ? list.filter(p => p.id !== player.id)
        : [...list, { id: player.id, name: player.name, position: player.position }];
      localStorage.setItem('academic_watchlist', JSON.stringify(updated));
      setOnWatchlist(!onWatchlist);
    } catch(e) { console.error(e); }
  };
  const [aiLoading,      setAiLoading     ] = useState(false);
  const [aiError,        setAiError       ] = useState('');
  const [fitScore,       setFitScore      ] = useState(null);
  const fetchRef = useRef(false);
  const printRef = useRef(null);

  useEffect(() => {
    setEp({
      name:player.name||'', position:player.position||'', dob:player.dob||'',
      height:player.height||'', weight:player.weight||'',
      high_school:player.high_school||'', hometown:player.hometown||'', hs_state:player.hs_state||'',
      gpa:player.gpa||'', ncaa_eligibility:player.ncaa_eligibility||'unknown',
      sat_score:player.sat_score||'', act_score:player.act_score||'',
      class_year:player.class_year||'', graduation_year:player.graduation_year||'',
      maxpreps_url:player.maxpreps_url||'', forty_time:player.forty_time||'',
      wingspan:player.wingspan||'', hand_size:player.hand_size||'', arm_length:player.arm_length||'',
      broad_jump:player.broad_jump||'', shuttle_time:player.shuttle_time||'',
      ten_yard_split:player.ten_yard_split||'', three_cone:player.three_cone||'',
      fg_range_yards:player.fg_range_yards||'', punt_hang_time:player.punt_hang_time||'',
      snap_time:player.snap_time||'',
      vertical_jump:player.vertical_jump||'', bench_press:player.bench_press||'',
      recruit_stars:player.recruit_stars||'', recruit_rating:player.recruit_rating||'',
    });
  }, [player]);

  useEffect(() => {
    authFetch(`${API}/api/players/${player.id}/fit-score`)
      .then(r=>r.ok?r.json():null).then(d=>{if(d)setFitScore(d);}).catch(()=>{});
  }, [player.id]);

  const s = (k,v) => setEp(p=>({...p,[k]:v}));

  const handlePhotoUpload = async e => {
    const file=e.target.files[0]; if(!file) return;
    setUploadingPhoto(true);
    try {
      const fd=new FormData(); fd.append('photo',file);
      const r=await authFetch(`${API}/api/players/${player.id}/photo`,{method:'POST',body:fd});
      if(r.ok){const d=await r.json();if(onPhotoUpload)onPhotoUpload(d.photo_url);}
    } finally { setUploadingPhoto(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates=Object.fromEntries(Object.entries(ep).filter(([,v])=>v!==''&&v!==null));
      const r=await authFetch(`${API}/api/players/${player.id}`,{method:'PATCH',
        headers:{'Content-Type':'application/json'},body:JSON.stringify(updates)});
      if(r.ok){const d=await r.json();if(onPlayerUpdate)onPlayerUpdate(d);setEditOpen(false);}
      else alert('Failed to save changes');
    } catch { alert('Failed to save changes'); }
    finally { setSaving(false); }
  };

  const doFetch = async () => {
    if(fetchRef.current) return; fetchRef.current=true;
    setAiLoading(true); setAiError(''); setAiSummary('');
    try {
      const r=await authFetch(`${API}/api/ai/${player.id}/summary`);
      if(r.ok){const d=await r.json();setAiSummary(d.summary);try{localStorage.setItem(`scout_report_${player.id}`,d.summary);}catch{}}
      else setAiError('Failed to generate. Please try again.');
    } catch { setAiError('Could not connect to AI service.'); }
    finally { setAiLoading(false); fetchRef.current=false; }
  };
  const regen = () => { try{localStorage.removeItem(`scout_report_${player.id}`);}catch{} fetchRef.current=false; doFetch(); };

  const fitPct = fitScore?.overall>0 ? ((fitScore.overall/9)*100).toFixed(1)+'%' : null;

  const handlePrint = () => {
    const win=window.open('','_blank');
    const body=aiSummary.split('\n').map(line=>{
      if(!line.trim()) return '';
      const t=line.trim();
      const h=t===t.toUpperCase()&&t.length>3&&/^[A-Z][A-Z\s&]+$/.test(t);
      return h?`<div class="sh">${t}</div>`:`<p>${line}</p>`;
    }).join('');
    win.document.write(`<!DOCTYPE html><html><head><title>Scout Report — ${player.name}</title><style>
      *{margin:0;padding:0;box-sizing:border-box;}body{font-family:Georgia,serif;color:#111;background:#fff;padding:52px;max-width:780px;margin:0 auto;}
      .hdr{display:flex;justify-content:space-between;border-bottom:2.5px solid #111;padding-bottom:16px;margin-bottom:28px;}
      .org{font-family:Arial,sans-serif;font-size:9px;letter-spacing:.15em;text-transform:uppercase;color:#888;margin-bottom:6px;}
      .name{font-size:30px;font-weight:700;}.meta{font-size:13px;color:#555;margin-top:5px;}
      .stats{display:flex;gap:18px;margin-top:10px;}.stat label{display:block;font-size:9px;text-transform:uppercase;color:#888;}.stat span{font-size:14px;font-weight:700;color:#1a3a6e;}
      .score-col{text-align:right;}.score{font-size:42px;font-weight:700;color:#1a3a6e;line-height:1;}
      .sh{font-size:9px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#1a3a6e;border-bottom:1px solid #ddd;padding-bottom:4px;margin:22px 0 8px;}
      p{font-size:13px;line-height:1.8;color:#222;margin-bottom:8px;}
      .footer{margin-top:40px;padding-top:10px;border-top:1px solid #ddd;display:flex;justify-content:space-between;font-size:10px;color:#aaa;}
    </style></head><body>
    <div class="hdr"><div>
      <div class="org">SchemeSync HS — Prospect Evaluation</div>
      <div class="name">${player.name}</div>
      <div class="meta">${player.position||''} • ${player.class_year||'HS'} • ${player.high_school||''} — ${player.hometown||''}${player.hs_state?', '+player.hs_state:''}</div>
      <div class="stats">
        ${player.height?`<div class="stat"><label>HT</label><span>${player.height}</span></div>`:''}
        ${player.weight?`<div class="stat"><label>WT</label><span>${player.weight} lbs</span></div>`:''}
        ${player.forty_time?`<div class="stat"><label>40</label><span>${player.forty_time}s</span></div>`:''}
        ${player.gpa?`<div class="stat"><label>GPA</label><span>${parseFloat(player.gpa).toFixed(2)}</span></div>`:''}
        ${player.recruit_stars?`<div class="stat"><label>Stars</label><span>${player.recruit_stars}★</span></div>`:''}
      </div></div>
      <div class="score-col">
        <div style="font-size:9px;text-transform:uppercase;color:#888;margin-bottom:4px">Program Fit</div>
        <div class="score">${fitPct||'N/A'}</div>
        ${fitScore?.tier?`<div style="font-size:11px;font-weight:600;color:#1a3a6e;margin-top:2px">${fitScore.tier}</div>`:''}
      </div>
    </div>${body}
    <div class="footer"><span>SchemeSync HS • Powered by AiDA AI</span><span>${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</span></div>
    </body></html>`);
    win.document.close(); win.focus(); setTimeout(()=>win.print(),400);
  };

  /* ─── card style ─────────────────────────────────────────────────────────── */
  const card = {background:'#0D1E36',border:'1px solid rgba(84,110,122,0.22)',borderRadius:12,padding:'18px 20px'};
  const sectionLabel = color => ({
    display:'flex',alignItems:'center',gap:7,marginBottom:14,
    color:color,fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.1em'
  });

  /* ─── RENDER ─────────────────────────────────────────────────────────────── */
  return (
    <div style={{display:'flex',flexDirection:'column',gap:14,width:'100%'}}>

      {/* ══════════════════════════════════════════════════
          EDIT MODAL — clean, doesn't touch the layout
      ══════════════════════════════════════════════════ */}
      {editOpen && (
        <div onClick={e=>{if(e.target===e.currentTarget)setEditOpen(false)}} style={{
          position:'fixed',inset:0,background:'rgba(0,0,0,0.78)',zIndex:60,padding:isMobile?0:16,alignItems:isMobile?'flex-end':'center',
          display:'flex',alignItems:'center',justifyContent:'center',
          zIndex:1000,backdropFilter:'blur(6px)'
        }}>
          <div style={{background:'#0B1828',border:'1px solid rgba(0,212,255,0.18)',
            borderRadius:16,width:isMobile?'100%':660,maxWidth:'100%',maxHeight:isMobile?'92vh':'90vh',overflow:'auto',
            boxShadow:'0 32px 80px rgba(0,0,0,0.7)'}}>

            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
              padding:'22px 26px',borderBottom:'1px solid rgba(84,110,122,0.18)'}}>
              <div>
                <div style={{color:'white',fontWeight:700,fontSize:18}}>Edit Prospect Profile</div>
                <div style={{color:'#546E7A',fontSize:12,marginTop:2}}>{player.name}</div>
              </div>
              <button onClick={()=>setEditOpen(false)} style={{background:'none',border:'none',color:'#546E7A',cursor:'pointer'}}>
                <X size={20}/>
              </button>
            </div>

            <div style={{padding:isMobile?'18px 16px':'24px 26px',display:'flex',flexDirection:'column',gap:22}}>

              <div>
                <div style={{color:'#00D4FF',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.12em',marginBottom:14,paddingBottom:6,borderBottom:'1px solid rgba(0,212,255,0.1)'}}>Identity</div>
                <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(4,1fr)',gap:12}}>
                  <MF span={2} label="Full Name"    value={ep.name}            onChange={e=>s('name',e.target.value)}/>
                  <MF         label="Position"      value={ep.position}        onChange={e=>s('position',e.target.value)}/>
                  <MS         label="Class Year"    value={ep.class_year}      onChange={e=>s('class_year',e.target.value)}>
                    <option value="">— Select —</option>
                    <option value="Freshman">Freshman</option><option value="Sophomore">Sophomore</option>
                    <option value="Junior">Junior</option><option value="Senior">Senior</option>
                  </MS>
                  <MF         label="Grad Year"     value={ep.graduation_year} onChange={e=>s('graduation_year',e.target.value)} type="number"/>
                  <MF         label="Date of Birth" value={ep.dob}             onChange={e=>s('dob',e.target.value)}             type="date"/>
                  <MF         label="Height"        value={ep.height}          onChange={e=>s('height',e.target.value)}          placeholder="6-2"/>
                  <MF         label="Weight (lbs)"  value={ep.weight}          onChange={e=>s('weight',e.target.value)}          type="number"/>
                </div>
              </div>

              <div>
                <div style={{color:'#00E676',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.12em',marginBottom:14,paddingBottom:6,borderBottom:'1px solid rgba(0,230,118,0.1)'}}>Academics</div>
                <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(4,1fr)',gap:12}}>
                  <MF span={2} label="High School"      value={ep.high_school}       onChange={e=>s('high_school',e.target.value)}/>
                  <MS          label="NCAA Eligibility" value={ep.ncaa_eligibility}  onChange={e=>s('ncaa_eligibility',e.target.value)}>
                    <option value="unknown">Unknown</option><option value="eligible">Eligible</option>
                    <option value="at_risk">At Risk</option><option value="ineligible">Ineligible</option>
                  </MS>
                  <MF          label="GPA"              value={ep.gpa}               onChange={e=>s('gpa',e.target.value)}              type="number" placeholder="3.85"/>
                  <MF          label="SAT Score"        value={ep.sat_score}         onChange={e=>s('sat_score',e.target.value)}        type="number"/>
                  <MF          label="ACT Score"        value={ep.act_score}         onChange={e=>s('act_score',e.target.value)}        type="number"/>
                  <MF          label="Hometown"         value={ep.hometown}          onChange={e=>s('hometown',e.target.value)}/>
                  <MF          label="State"            value={ep.hs_state}          onChange={e=>s('hs_state',e.target.value)}         placeholder="AL"/>
                </div>
              </div>

              <div>
                <div style={{color:'#FFB300',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.12em',marginBottom:14,paddingBottom:6,borderBottom:'1px solid rgba(255,179,0,0.1)'}}>Athletic Measurables</div>
                <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(4,1fr)',gap:12}}>
                  {measurableDefs
                    .filter(m => m.field_key !== 'height' && m.field_key !== 'weight')
                    .map(m => (
                      <MF key={m.field_key}
                        label={m.unit ? `${m.display_name} (${m.unit})` : m.display_name}
                        value={ep[m.field_key]}
                        onChange={e=>s(m.field_key, e.target.value)}
                        inputMode={m.input_type === 'text' ? undefined : 'decimal'}
                        placeholder={m.placeholder || ''}/>
                    ))}
                  <MF label="Recruit Stars"   value={ep.recruit_stars}  onChange={e=>s('recruit_stars',e.target.value)}  inputMode="numeric" placeholder="1–5"/>
                  <MF label="Recruit Rating"  value={ep.recruit_rating} onChange={e=>s('recruit_rating',e.target.value)}  inputMode="numeric"/>
                  <MF span={3} label="MaxPreps URL" value={ep.maxpreps_url} onChange={e=>s('maxpreps_url',e.target.value)} type="url" placeholder="https://maxpreps.com/athlete/..."/>
                </div>
              </div>
            </div>

            <div style={{display:'flex',justifyContent:'flex-end',gap:10,
              padding:'16px 26px',borderTop:'1px solid rgba(84,110,122,0.18)'}}>
              <button onClick={()=>setEditOpen(false)} style={{
                display:'flex',alignItems:'center',gap:6,background:'transparent',
                border:'1px solid rgba(84,110,122,0.4)',color:'#8AAABB',
                borderRadius:8,padding:'9px 18px',fontSize:13,cursor:'pointer'}}>
                <X size={13}/> Cancel
              </button>
              <button onClick={handleSave} disabled={saving} style={{
                display:'flex',alignItems:'center',gap:6,background:'#00D4FF',
                border:'none',color:'#04101E',fontWeight:700,
                borderRadius:8,padding:'9px 22px',fontSize:13,cursor:'pointer'}}>
                <Save size={13}/> {saving?'Saving…':'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          HERO BANNER — like Fit Analysis PlayerHeader
      ══════════════════════════════════════════════════ */}
      <div className="panel-card" style={{padding:0,overflow:'hidden',position:'relative'}}>
        <div style={{position:'absolute',top:0,left:0,right:0,height:3,
          background:'linear-gradient(90deg,transparent 0%,rgba(0,212,255,0.7) 40%,rgba(0,230,118,0.5) 70%,transparent 100%)'}}/>

        <div style={{display:'flex',gap:isMobile?16:24,padding:isMobile?'20px 16px':'28px 32px',alignItems:'center',flexDirection:isMobile?'column':'row',textAlign:isMobile?'center':'left'}}>

          {/* Photo */}
          <div style={{position:'relative',flexShrink:0}} className="group">
            <div style={{width:isMobile?88:100,height:isMobile?106:120,borderRadius:12,overflow:'hidden',
              background:'#12233D',border:'2px solid rgba(84,110,122,0.3)',
              display:'flex',alignItems:'center',justifyContent:'center'}}>
              {player.photo_url
                ? <img src={player.photo_url.startsWith('http')?player.photo_url:`${API}${player.photo_url}`}
                    alt={player.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                : <User size={40} color="#2A4060"/>}
            </div>
            <label style={{position:'absolute',inset:0,borderRadius:12,background:'rgba(0,0,0,0.62)',
              opacity:0,transition:'opacity .2s',cursor:'pointer',
              display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:4}} className="group-hover:opacity-100">
              <Upload size={14} color="white"/>
              <span style={{color:'white',fontSize:10}}>{uploadingPhoto?'…':'Upload'}</span>
              <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{display:'none'}}/>
            </label>
          </div>

          {/* Main identity */}
          <div style={{flex:1,minWidth:0}}>
            <div style={{color:'#546E7A',fontSize:9,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:8}}>
              SchemeSync HS — Prospect Profile
            </div>
            <h1 style={{color:'white',fontWeight:800,fontSize:isMobile?26:36,lineHeight:1.1,letterSpacing:'-0.02em',marginBottom:10,overflowWrap:'anywhere'}}>
              {player.name}
            </h1>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:18,flexWrap:'wrap',justifyContent:isMobile?'center':'flex-start'}}>
              {player.position && (
                <span style={{background:'rgba(0,212,255,0.12)',color:'#00D4FF',
                  border:'1px solid rgba(0,212,255,0.25)',borderRadius:6,
                  padding:'3px 12px',fontSize:13,fontWeight:700,letterSpacing:'0.04em'}}>
                  {player.position}
                </span>
              )}
              {player.class_year && <span style={{color:'#B0BEC5',fontSize:14}}>{player.class_year}</span>}
              {player.high_school && <span style={{color:'#B0BEC5',fontSize:14}}>• {player.high_school}</span>}
              {player.hometown && (
                <span style={{color:'#546E7A',fontSize:13}}>
                  • {player.hometown}{player.hs_state&&`, ${player.hs_state}`}
                </span>
              )}
              {player.ncaa_eligibility && player.ncaa_eligibility !== 'unknown' && (
                <span style={{
                  padding:'3px 10px',borderRadius:5,fontSize:11,fontWeight:700,marginLeft:4,
                  color:eligColor(player.ncaa_eligibility),
                  border:`1px solid ${eligColor(player.ncaa_eligibility)}40`,
                  background:`${eligColor(player.ncaa_eligibility)}15`}}>
                  {eligLabel(player.ncaa_eligibility)}
                </span>
              )}
            </div>
            {/* Stats row */}
            <div style={{display:'flex',gap:28,flexWrap:'wrap'}}>
              <StatChip label="HT"    value={player.height}/>
              <StatChip label="WT"    value={player.weight?`${player.weight}`:null}/>
              <StatChip label="GPA"   value={player.gpa?parseFloat(player.gpa).toFixed(2):null} color={gpaColor(player.gpa)}/>
              <StatChip label="40"    value={player.forty_time?`${player.forty_time}s`:null}/>
              <StatChip label="Vert"  value={player.vertical_jump?`${player.vertical_jump}"`:null}/>
              <StatChip label="Stars" value={player.recruit_stars?`${player.recruit_stars}★`:null} color="#FFB300"/>
            </div>
          </div>

          {/* Right: fit score + edit */}
          <div style={{flexShrink:0,display:'flex',flexDirection:'column',alignItems:isMobile?'center':'flex-end',gap:16,width:isMobile?'100%':'auto'}}>
            {fitPct ? (
              <div style={{textAlign:'right'}}>
                <div style={{color:'#546E7A',fontSize:9,textTransform:'uppercase',letterSpacing:'0.12em',marginBottom:6}}>
                  Program Fit Score
                </div>
                <div style={{color:'#00E676',fontWeight:800,fontSize:52,fontFamily:'monospace',lineHeight:1}}>
                  {fitPct}
                </div>
                {fitScore?.tier && (
                  <div style={{color:'#00E676',fontSize:12,fontWeight:600,marginTop:5,letterSpacing:'0.06em'}}>
                    {fitScore.tier}
                  </div>
                )}
                {fitScore?.highlights?.slice(0,3).map((h,i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:4,justifyContent:'flex-end',marginTop:4}}>
                    <ChevronRight size={10} color="#00E676"/>
                    <span style={{color:'#7DCCA0',fontSize:11}}>{h}</span>
                  </div>
                ))}
              </div>
            ) : <div/>}

            <button onClick={()=>setEditOpen(true)} style={{
              display:'flex',alignItems:'center',gap:6,background:'transparent',
              border:'1px solid rgba(84,110,122,0.4)',color:'#8AAABB',
              borderRadius:8,padding:'8px 18px',fontSize:12,cursor:'pointer',whiteSpace:'nowrap'}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(0,212,255,0.5)';e.currentTarget.style.color='#00D4FF';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='rgba(84,110,122,0.4)';e.currentTarget.style.color='#8AAABB';}}>
              <Edit2 size={13}/> Edit Profile
            </button>
            <button onClick={toggleWatchlist} style={{
              display:'flex',alignItems:'center',gap:6,
              background: onWatchlist ? 'rgba(126,87,194,0.15)' : 'transparent',
              border: onWatchlist ? '1px solid rgba(126,87,194,0.6)' : '1px solid rgba(84,110,122,0.4)',
              color: onWatchlist ? '#7E57C2' : '#8AAABB',
              borderRadius:8,padding:'8px 18px',fontSize:12,cursor:'pointer',whiteSpace:'nowrap'}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(126,87,194,0.6)';e.currentTarget.style.color='#7E57C2';}}
              onMouseLeave={e=>{if(!onWatchlist){e.currentTarget.style.borderColor='rgba(84,110,122,0.4)';e.currentTarget.style.color='#8AAABB';}}}>
              {onWatchlist ? <><BookmarkCheck size={13}/> On Watchlist</> : <><BookmarkPlus size={13}/> Add to Academic Tracking</>}
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════
          BODY: left data cards + right AI report
      ══════════════════════════════════════════════════ */}
      <div style={{display:'flex',gap:14,alignItems:'flex-start',flexDirection:isMobile?'column':'row'}}>

        {/* ── LEFT: data cards ─────────────────────────── */}
        <div style={{width:isMobile?'100%':350,flexShrink:0,display:'flex',flexDirection:'column',gap:12}}>

          {/* Academic Standing */}
          <div style={card}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
              <div style={{display:'flex',alignItems:'center',gap:7}}>
                <GraduationCap size={15} color="#00E676"/>
                <span style={{color:'white',fontWeight:700,fontSize:14}}>Academic Standing</span>
              </div>
              {player.ncaa_eligibility && (
                <span style={{
                  padding:'2px 9px',borderRadius:5,fontSize:11,fontWeight:700,
                  color:eligColor(player.ncaa_eligibility),
                  border:`1px solid ${eligColor(player.ncaa_eligibility)}40`,
                  background:`${eligColor(player.ncaa_eligibility)}18`}}>
                  {eligLabel(player.ncaa_eligibility)}
                </span>
              )}
            </div>
            <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'2fr 1fr 1fr',gap:'12px 16px',marginBottom:12}}>
              <Cell label="High School" value={player.high_school}/>
              <Cell label="GPA"         value={player.gpa?parseFloat(player.gpa).toFixed(2):null} color={gpaColor(player.gpa)} big/>
              <Cell label="NCAA"        value={eligLabel(player.ncaa_eligibility)} color={eligColor(player.ncaa_eligibility)}/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr 1fr':'1fr 1fr 2fr',gap:'10px 16px',paddingTop:10,borderTop:'1px solid rgba(84,110,122,0.12)'}}>
              <Cell label="SAT"      value={player.sat_score}/>
              <Cell label="ACT"      value={player.act_score}/>
              <Cell label="Hometown" value={player.hometown?`${player.hometown}${player.hs_state?', '+player.hs_state:''}`:null}/>
            </div>
          </div>

          {/* Personal Details */}
          <div style={card}>
            <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:14}}>
              <User size={15} color="#00D4FF"/>
              <span style={{color:'white',fontWeight:700,fontSize:14}}>Personal Details</span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(3,1fr)',gap:'12px 16px'}}>
              <Cell label="Position"  value={player.position}/>
              <Cell label="Class"     value={player.class_year}/>
              <Cell label="Grad Year" value={player.graduation_year}/>
              <Cell label="DOB"       value={fmtDOB(player.dob)}/>
              <Cell label="Height"    value={player.height}/>
              <Cell label="Weight"    value={player.weight?`${player.weight} lbs`:null}/>
            </div>
          </div>

          {/* Athletic Measurables */}
          <div style={card}>
            <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:14}}>
              <TrendingUp size={15} color="#FFB300"/>
              <span style={{color:'white',fontWeight:700,fontSize:14}}>Athletic Measurables</span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(3,1fr)',gap:'12px 16px'}}>
              {measurableDefs
                .filter(m => m.field_key !== 'height' && m.field_key !== 'weight')
                .map(m => (
                  <Cell key={m.field_key} label={m.display_name}
                    value={player[m.field_key] != null && player[m.field_key] !== ''
                      ? `${player[m.field_key]}${m.unit === 's' ? 's' : m.unit === 'in' ? '"' : m.unit ? ' ' + m.unit : ''}`
                      : null}
                    big/>
                ))}
              <Cell label="Recruit Stars"  value={player.recruit_stars?`${player.recruit_stars}★`:null}  big color="#FFB300"/>
              <Cell label="Recruit Rating" value={player.recruit_rating||null}                           big/>
              <div style={{display:'flex',flexDirection:'column',justifyContent:'flex-end',paddingBottom:2}}>
                {player.maxpreps_url
                  ? <a href={player.maxpreps_url} target="_blank" rel="noopener noreferrer"
                      style={{display:'flex',alignItems:'center',gap:5,color:'#00D4FF',fontSize:12,textDecoration:'none'}}>
                      <ExternalLink size={11}/> MaxPreps
                    </a>
                  : <span style={{color:'#263A50',fontSize:12}}>No MaxPreps</span>}
                <div style={{color:'#1A2E42',fontSize:9,marginTop:8}}>
                  ID {player.id}{player.created_at&&` • ${new Date(player.created_at).toLocaleDateString()}`}
                </div>
              </div>
            </div>
          </div>

          {/* Top Strengths — only shown when fitScore is available */}
          {fitScore?.dimensionScores && Object.keys(fitScore.dimensionScores).length > 0 && (
            <div style={card}>
              <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:14}}>
                {/* Cyan left accent bar like the Fit Analysis strengths panel */}
                <div style={{width:3,height:16,borderRadius:2,background:'#00D4FF',flexShrink:0}}/>
                <span style={{color:'white',fontWeight:700,fontSize:14}}>Top Strengths</span>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:2}}>
                {Object.entries(fitScore.dimensionScores)
                  .sort(([,a],[,b]) => b - a)
                  .slice(0, 4)
                  .map(([name, score]) => {
                    const pct = Math.round((score / 9) * 100);
                    const color = score >= 8 ? '#00E676' : score >= 6.5 ? '#FFD600' : '#FF7043';
                    return (
                      <div key={name} style={{
                        display:'flex',alignItems:'center',justifyContent:'space-between',
                        padding:'9px 12px',borderRadius:8,background:'rgba(255,255,255,0.03)',
                        border:'1px solid rgba(84,110,122,0.12)',
                      }}>
                        <span style={{color:'#B0BEC5',fontSize:13}}>{name}</span>
                        <span style={{color,fontWeight:700,fontSize:16,fontFamily:'monospace'}}>{score.toFixed(1)}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT: AI Scout Report ────────────────────── */}
        <div style={{flex:1,minWidth:0,width:isMobile?'100%':'auto',height:isMobile?'auto':'calc(100vh - 340px)',minHeight:isMobile?420:undefined,display:'flex',flexDirection:'column'}}>

          {/* Toolbar */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,padding:'0 4px'}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <Brain size={18} color="#7E57C2"/>
              <span style={{color:'white',fontWeight:700,fontSize:16}}>AI Scout Report</span>
            </div>
            <div style={{display:'flex',gap:8}}>
              {aiSummary && (
                <button onClick={handlePrint} style={{
                  display:'flex',alignItems:'center',gap:5,background:'transparent',
                  border:'1px solid rgba(84,110,122,0.4)',color:'#8AAABB',
                  borderRadius:8,padding:'6px 14px',fontSize:12,cursor:'pointer'}}>
                  <Printer size={12}/> Print Report
                </button>
              )}
              {aiSummary && (
                <button onClick={regen} disabled={aiLoading}
                  style={{background:'none',border:'none',color:'#546E7A',cursor:'pointer',padding:4}}>
                  <RefreshCw size={14} className={aiLoading?'animate-spin':''}/>
                </button>
              )}
            </div>
          </div>

          {/* Document pane */}
          <div ref={printRef} style={{
            flex:1,overflowY:'auto',borderRadius:14,
            background:'linear-gradient(160deg,#0D1E36 0%,#0A1628 100%)',
            border:'1px solid rgba(84,110,122,0.22)',
            boxShadow:'0 4px 32px rgba(0,0,0,0.45)',
            padding:'36px 40px',
          }}>

            {aiLoading && (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',gap:16}}>
                <Brain size={48} color="#7E57C2" className="animate-pulse"/>
                <div style={{color:'white',fontWeight:600,fontSize:15}}>Generating Scout Report…</div>
                <div style={{color:'#546E7A',fontSize:13}}>AiDA AI is analyzing all available prospect data</div>
              </div>
            )}

            {aiError && !aiLoading && (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',gap:12}}>
                <div style={{color:'#EF5350',fontSize:13}}>{aiError}</div>
                <button onClick={regen} className="btn-secondary" style={{fontSize:12}}>Try Again</button>
              </div>
            )}

            {aiSummary && !aiLoading && (
              <>
                <div style={{borderBottom:'2px solid rgba(0,212,255,0.2)',paddingBottom:20,marginBottom:24}}>
                  <div style={{color:'#546E7A',fontSize:9,letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:6}}>
                    SchemeSync HS — Prospect Evaluation
                  </div>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between'}}>
                    <div>
                      <div style={{color:'white',fontSize:24,fontWeight:800,lineHeight:1.1,letterSpacing:'-0.01em'}}>{player.name}</div>
                      <div style={{color:'#B0BEC5',fontSize:13,marginTop:4}}>
                        {player.position} • {player.class_year||'HS'}
                        {player.high_school&&` • ${player.high_school}`}
                        {player.hometown&&` — ${player.hometown}`}{player.hs_state&&`, ${player.hs_state}`}
                      </div>
                      <div style={{display:'flex',gap:20,marginTop:12}}>
                        {[
                          {l:'HT',   v:player.height},
                          {l:'WT',   v:player.weight?`${player.weight} lbs`:null},
                          {l:'40',   v:player.forty_time?`${player.forty_time}s`:null},
                          {l:'GPA',  v:player.gpa?parseFloat(player.gpa).toFixed(2):null},
                          {l:'Stars',v:player.recruit_stars?`${player.recruit_stars}★`:null},
                        ].filter(x=>x.v).map(x=>(
                          <div key={x.l}>
                            <div style={{color:'#546E7A',fontSize:9,textTransform:'uppercase',letterSpacing:'0.1em'}}>{x.l}</div>
                            <div style={{color:'#00D4FF',fontWeight:700,fontSize:14}}>{x.v}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {fitPct && (
                      <div style={{textAlign:'right',flexShrink:0,marginLeft:24}}>
                        <div style={{color:'#546E7A',fontSize:9,textTransform:'uppercase',letterSpacing:'0.12em',marginBottom:4}}>Program Fit</div>
                        <div style={{color:'#00E676',fontWeight:800,fontSize:36,fontFamily:'monospace',lineHeight:1}}>{fitPct}</div>
                        {fitScore?.tier&&<div style={{color:'#00E676',fontSize:10,fontWeight:600,marginTop:3}}>{fitScore.tier}</div>}
                      </div>
                    )}
                  </div>
                </div>

                <ReportContent text={aiSummary}/>

                <div style={{marginTop:32,paddingTop:12,borderTop:'1px solid rgba(84,110,122,0.15)',display:'flex',justifyContent:'space-between'}}>
                  <span style={{color:'#546E7A',fontSize:11}}>Powered by AiDA AI</span>
                  <span style={{color:'#546E7A',fontSize:11}}>{new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</span>
                </div>
              </>
            )}

            {!aiSummary && !aiLoading && !aiError && (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',gap:20}}>
                <Brain size={64} color="#7E57C2" style={{opacity:.18}}/>
                <div style={{textAlign:'center'}}>
                  <div style={{color:'white',fontWeight:700,fontSize:20,marginBottom:8}}>AI Scout Report</div>
                  <div style={{color:'#546E7A',fontSize:13,maxWidth:360,lineHeight:1.65}}>
                    Generate a comprehensive scouting report powered by AiDA AI, based on all available prospect data, evaluation grades, and coach notes.
                  </div>
                </div>
                <button onClick={doFetch} className="btn-primary"
                  style={{display:'flex',alignItems:'center',gap:8,padding:'12px 28px',fontSize:14,fontWeight:700}}>
                  <Brain size={16}/> Generate AI Report
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerProfile;
